import type { OfferView, VipLevelView, VipNumberKey, VipPackState, VipPageView } from "@highmarch/app";
import { OfferDef, OfferItem, VipCrest } from "@highmarch/domain";
import { fmtMinutes } from "../deals/dealsModel";

/**
 * The VIP page's words and states (VIP-UI, docs/SHOP.md §8; Kingshot's VIP screen): pure functions over the app's
 * `VipPageView`, so every string and state the page draws is tested without a DOM. The page itself is vipPage.ts.
 */

/** One benefit row as the page lists it. */
export interface BenefitRow {
  readonly key: VipNumberKey;
  readonly label: string;
  /** "+16%", "+10 h", "≤ 1h", "3 a day". */
  readonly value: string;
  /** Combat rows (tower damage, fortress health, troops) lead the list, in green: the power a level adds. */
  readonly combat: boolean;
  /** A percent the page paints green like the combat rows (build speed, resources). */
  readonly percent: boolean;
  /** This level raises it over the one before: the NEW marker. */
  readonly isNew: boolean;
  /** What it does, for the tap-to-explain bubble. */
  readonly detail: string;
}

interface RowDef {
  readonly key: VipNumberKey;
  readonly label: string;
  readonly detail: string;
  readonly combat?: boolean;
  readonly fmt: (n: number) => string;
}

const pct = (n: number): string => `+${n}%`;
const plus = (n: number): string => `+${n}`;

/** Row order: combat first, then build speed and production, then the quality-of-life perks (docs/SHOP.md §8.3). */
export const BENEFIT_ROWS: readonly RowDef[] = [
  { key: "towerDamagePercent", label: "Tower damage", combat: true, fmt: pct, detail: "Every tower hits harder, the fortress's own included" },
  { key: "fortressHpPercent", label: "Fortress health", combat: true, fmt: pct, detail: "Your fortress takes more hits before it falls" },
  { key: "troopPercent", label: "Troop strength", combat: true, fmt: pct, detail: "Tougher troops that hit harder" },
  { key: "buildSpeedPercent", label: "Build speed", fmt: pct, detail: "New upgrades finish sooner" },
  { key: "resourcePercent", label: "Gold production", fmt: pct, detail: "More gold from Tribute and battle chests" },
  { key: "tributeExtraHours", label: "Tribute storage", fmt: (n) => `+${n} h`, detail: "Tribute keeps filling longer while you're away" },
  { key: "extraReductionsPerDay", label: "Timer cuts a day", fmt: plus, detail: "More timer cuts each day" },
  { key: "freeFinishMinutes", label: "Free finish", fmt: (n) => `≤ ${fmtMinutes(n)}`, detail: "An upgrade this close to done finishes free" },
  { key: "extraQueuePerBuilder", label: "Upgrade queue", fmt: plus, detail: "More upgrades queued behind each builder" },
  { key: "extraOrderRerolls", label: "Order swaps", fmt: plus, detail: "More Daily Order swaps" },
  { key: "extraDealChanges", label: "Daily Deal changes", fmt: plus, detail: "Change the Daily Deals theme more often" },
  { key: "extraCrateSlots", label: "Gold packs", fmt: plus, detail: "More gold packs a day" },
  { key: "freeMerchantRefreshes", label: "Free Merchant refreshes", fmt: (n) => `${n} a day`, detail: "Restock the Merchant free before paying gems" },
];

/** The browsed level's benefits, every nonzero one, in the page's order, with NEW on what this level raises. */
export function benefitRows(l: VipLevelView): BenefitRow[] {
  const raised = new Set<VipNumberKey>(l.raised);
  return BENEFIT_ROWS.filter((r) => l.benefits[r.key] > 0).map((r) => ({
    key: r.key, label: r.label, value: r.fmt(l.benefits[r.key]), combat: r.combat === true, percent: r.fmt === pct, isNew: raised.has(r.key), detail: r.detail,
  }));
}

/** The level the browser opens on: the current one (VIP 0 opens on VIP 1, the first goal). */
export function startLevel(v: VipPageView): number {
  return clampLevel(v, Math.max(1, v.level));
}

/** Levels 1..n (n = 12); arrows stop at the ends. */
export function clampLevel(v: VipPageView, level: number): number {
  return Math.max(1, Math.min(v.levels.length, Math.round(level)));
}

export function levelView(v: VipPageView, level: number): VipLevelView | null {
  return v.levels[clampLevel(v, level) - 1] ?? null;
}

/** The crest tier of a level (VIP 0 wears none: the bronze one greyed). */
export function crestOf(v: VipPageView, level: number): VipCrest {
  return level <= 0 ? "none" : levelView(v, level)?.benefits.crest ?? "none";
}

/** Under the badge: "Maxed" at the top, else the XP to the next level. */
export function badgeNote(v: VipPageView): string {
  return v.nextAt === null ? "Maxed" : `${v.xp.toLocaleString("en-US")} / ${v.nextAt.toLocaleString("en-US")}`;
}

/** The XP bar's words. */
export function xpLine(v: VipPageView): string {
  return v.nextAt === null
    ? `${v.xp.toLocaleString("en-US")} VIP XP · top level`
    : `${(v.nextAt - v.xp).toLocaleString("en-US")} VIP XP to VIP ${v.level + 1}`;
}

/** Today's VIP XP claim as the level card draws it: "Today" (or "Day n" on a streak), its XP, and tomorrow's. */
export interface DailyXpRow {
  readonly label: string;
  readonly today: string;
  readonly tomorrow: string;
  readonly ready: boolean;
}

export function dailyXpRow(v: VipPageView): DailyXpRow {
  const xp = (n: number) => `+${n.toLocaleString("en-US")}`;
  return { label: v.streakDays > 1 ? `Day ${v.streakDays.toLocaleString("en-US")}` : "Today", today: xp(v.daily.xp), tomorrow: xp(v.tomorrowXp), ready: v.daily.ready };
}

/**
 * The daily bundle card for the browsed level: `claim` (today's, at the current level, ready), `claimed` (taken today:
 * the countdown to 00:00 UTC), `below` (a lower level's bundle: yours is bigger), `locked` (a level above: "Reach
 * VIP n"), `none` (a level without a bundle).
 */
export type BundleState = "claim" | "claimed" | "below" | "locked" | "none";

export function bundleState(v: VipPageView, level: number): BundleState {
  const items = bundleItems(v, level);
  if (items.length === 0) return "none";
  if (level > v.level) return "locked";
  if (level < v.level) return "below";
  return v.bundle.ready ? "claim" : "claimed";
}

/** The browsed level's bundle items (the current level's are the app's own `bundle.items`). */
export function bundleItems(v: VipPageView, level: number): readonly OfferItem[] {
  if (level === v.level && v.level > 0) return v.bundle.items;
  return levelView(v, level)?.benefits.dailyBundle ?? [];
}

/** The line on the bundle card. */
export function bundleNote(state: BundleState, level: number): string {
  switch (state) {
    case "claim": return "Free today: tap to claim";
    case "claimed": return "Claimed today · next one in";
    case "below": return "A lower level's bundle: yours is bigger";
    case "locked": return `Reach VIP ${level} for this bundle every day`;
    case "none": return "";
  }
}

/** The Special Pack card for the browsed level. `pack` is the app's offer view (null while locked or hidden). */
export interface PackCard {
  readonly state: VipPackState;
  readonly level: number;
  readonly title: string;
  readonly name: string;
  readonly offerId: string;
  readonly items: readonly OfferItem[];
  readonly pack: OfferView | null;
  /** The subtitle: "Purchased", "Reach VIP n", "Once only · stays until you buy it". */
  readonly note: string;
}

/** Null when the pack is hidden (the app's packState: no paid offers on the web without a store, a no-random storefront; no pack). */
export function packCard(v: VipPageView, level: number, def: OfferDef | null): PackCard | null {
  const l = levelView(v, level);
  if (!l || !def || l.packState === "hidden") return null;
  const note = l.packState === "purchased" ? "Bought" : l.packState === "locked" ? `Reach VIP ${l.level} to unlock` : "Once only · it waits for you";
  return { state: l.packState, level: l.level, title: `VIP ${l.level} Special Pack`, name: def.name, offerId: def.id, items: l.pack?.items ?? def.items, pack: l.pack, note };
}

/** The red count on the VIP page's way in (the VIP pill, the Store's VIP chip, the Shop tab's VIP row): today's VIP XP unclaimed, plus today's bundle waiting. */
export function vipDot(v: VipPageView): number {
  return (v.daily.ready ? 1 : 0) + (v.level > 0 && v.bundle.ready && v.bundle.items.length > 0 ? 1 : 0);
}
